import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import { getUserQueryKey } from '@/utils/query-keys';
import {
  QueryObserver,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export type User = {
  id: string;
  email: string;
  name: string | null;
  active_team_id: string | null;
  image: string | null;
  created_at: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
};

export const useUser = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: getUserQueryKey(),
    queryFn: async (): Promise<User> => {
      const { data, error } = await callApi(supabase, '/users/me');

      if (error) throw error;
      if (!data) throw new Error('No user found');

      return data;
    },
  });

  const updateUser = useMutation({
    mutationFn: async (data: {
      name?: string;
      active_team_id?: string;
    }): Promise<User> => {
      const { data: updatedUser, error } = await callApi(
        supabase,
        '/users/me',
        {
          method: 'PUT',
          data,
        },
      );

      if (error) throw error;
      if (!updatedUser) throw new Error('Failed to update user');

      return updatedUser;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: getUserQueryKey() });

      // Wait for the query to finish refreshing
      await new Promise<void>((resolve) => {
        const observer = new QueryObserver(queryClient, {
          queryKey: getUserQueryKey(),
        });

        const unsubscribe = observer.subscribe((result) => {
          if (!result.isFetching) {
            unsubscribe();
            resolve();
          }
        });
      });
    },
  });

  const requestAccountDeletion = useMutation({
    mutationFn: async () => {
      const { error } = await callApi(supabase, '/users/delete-request', {
        method: 'POST',
      });

      if (error) throw error;
    },
  });

  return {
    ...query,
    updateUser,
    requestAccountDeletion,
  };
};
